import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { useMutation, useQuery } from 'react-apollo';

import { GET_SIGNUP_INFO, UPSERT_SIGNUP_INFO_M } from '../../apollo/api';
import { useUserInfo, useRememberedAdminUserInfo } from '../../services';
import { isHideFeature } from '../../helpers/white-label-helper';
import { WhiteLabelFeaturesEnum } from '../../helpers/white-label.enum';

import WelcomeDialog from '../molecules/welcome/welcome-dialog';
import SignupInfoForm from '../molecules/welcome/signup-info-dialog';

import AddWorkspaceMember from './add-workspace-member';

const SKIP_WELCOME_WHITE_LIST = ['cirrusled.com'];

const SKIP_LIMIT_DAYS = 30;

const getIsSkipExpired = (createdDate: string): boolean => {
  const givenDate = new Date(createdDate);
  const currentDate = new Date();

  const givenDateUTC = Date.UTC(
    givenDate.getUTCFullYear(),
    givenDate.getUTCMonth(),
    givenDate.getUTCDate(),
    givenDate.getUTCHours(),
    givenDate.getUTCMinutes(),
    givenDate.getUTCSeconds()
  );

  const currentDateUTC = Date.UTC(
    currentDate.getUTCFullYear(),
    currentDate.getUTCMonth(),
    currentDate.getUTCDate(),
    currentDate.getUTCHours(),
    currentDate.getUTCMinutes(),
    currentDate.getUTCSeconds()
  );

  const differenceInMs = currentDateUTC - givenDateUTC;

  // Convert the difference to days (1 day = 24 * 60 * 60 * 1000 ms)
  const differenceInDays = differenceInMs / (24 * 60 * 60 * 1000);

  return differenceInDays > SKIP_LIMIT_DAYS;
};

const WelcomeFlow: FC = () => {
  const {
    me: {
      id: userId,
      email,
    }
  } = useUserInfo();
  const { rememberedAdminUser } = useRememberedAdminUserInfo() || {};

  const [step, setStep] = useState(0);
  const [shouldShowWelcomeFlow, setShouldShowWelcomeFlow] = useState<boolean>(false);
  const [canSkip, setCanSkip] = useState<boolean>(false);
  const [isSkipExpired, setIsSkipExpired] = useState<boolean>(false);

  const handleNextStep = useCallback(() => setStep((prev) => prev + 1), [setStep]);
  const handleCancel = useCallback(() => { setShouldShowWelcomeFlow(true) }, [setShouldShowWelcomeFlow]);

  const isHideWelcomeFlow = isHideFeature(WhiteLabelFeaturesEnum.WELCOME_FLOW);

  const { loading: isSignupInfoLoading, error: fetchSignupInfoError, data: signupInfoData } = useQuery(
    GET_SIGNUP_INFO, {
      fetchPolicy: 'cache-and-network',
      variables: { userId },
      skip: !userId || isHideWelcomeFlow,
      onCompleted: (data) => {
        const hasSkip = data?.getSignupInfo?.hasSkip;
        const createdAt = data?.getSignupInfo?.createdAt;

        if (hasSkip && createdAt) {
          const isSkipExpired = getIsSkipExpired(createdAt);

          setIsSkipExpired(isSkipExpired);
        }

        setCanSkip((canSkipState) => {
          if (hasSkip && createdAt) {
            const isSkipExpired = getIsSkipExpired(createdAt);

            return !isSkipExpired;
          } else {
            return !hasSkip;
          }
        });
      }
    });

  useEffect(() => {
    const isInwhitelist = SKIP_WELCOME_WHITE_LIST.some((whitelist) => (email.endsWith(whitelist)));
    const hasInvalidSIgnupInfo = (
      (signupInfoData?.getSignupInfo?.userId && isSkipExpired) ||
      !signupInfoData?.getSignupInfo?.userId
    );

    setShouldShowWelcomeFlow(
      !isSignupInfoLoading &&
      !rememberedAdminUser &&
      !isHideWelcomeFlow &&
      !isInwhitelist &&
      hasInvalidSIgnupInfo
    );
  }, [isSignupInfoLoading, signupInfoData?.getSignupInfo?.userId,rememberedAdminUser, isHideWelcomeFlow, email, isSkipExpired]);

  const [upsertSignupInfo] = useMutation(UPSERT_SIGNUP_INFO_M);

  const onSkip = useCallback(async () => {
    // Insert mock data for has_skip=true row as placeholder
    await upsertSignupInfo({
      variables: {
        userId,
        phone: 'xxxxxxxxxx',
        roleId: 1,
        industryId: 1,
        regionId: 1,
        hasSkip: true
      }
    });

    setShouldShowWelcomeFlow(false);
  }, [upsertSignupInfo, setShouldShowWelcomeFlow]);

  if (shouldShowWelcomeFlow) {
    return (
        <Box>
          {step === 0 && <WelcomeDialog onStart={handleNextStep} />}
          {step === 1 && <SignupInfoForm onClose={handleNextStep} canSkip={canSkip} onSkip={onSkip}/>}
          {step === 2 && <AddWorkspaceMember isOpen= {step === 2} onCancel={handleCancel} shouldShowSkip={true} />}
        </Box>
    );
  } else {
    return null;
  }
};

export default WelcomeFlow;